import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { eventBus } from '../eventBus';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Reprocessos_CriarInput.ts';
import '../contracts/Reprocessos_CompletarInput.ts';

import '../contracts/Reprocessos_CompletarResponse';
import '../contracts/Reprocessos_CriarResponse';
import type { Reprocessos_ListaResponseDados } from '../contracts/Reprocessos_ListaResponse';
import '../contracts/Reprocessos_ListaResponse';
import '../contracts/Reprocessos_UploadPhotoInput';
import '../contracts/Reprocessos_UploadPhotoResponse';
import type { Reprocessos_CompletarInput } from '../contracts/Reprocessos_CompletarInput';
import type { Reprocessos_CriarInput } from '../contracts/Reprocessos_CriarInput';
/**
 * Composable for SO Reprocesso (rework) operations.
 * Manages quality rework events linked to production steps.
 */
export function useSOReprocessos() {
  const isLoading = ref(false);
  const error = ref(null);
  const reprocessos = ref<Reprocessos_ListaResponseDados>([]);
  const basePath = '/index.php/so/reprocessos';

  const uploadPhoto = async (file: File) => {
    log('useSOReprocessos', 'uploadPhoto', 'Uploading reprocesso photo', { fileName: file.name, size: file.size });
    isLoading.value = true;
    error.value = null;
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await httpService.post(`${basePath}/upload_photo`, formData, { inputContract: 'Reprocessos_UploadPhotoInput', responseContract: 'Reprocessos_UploadPhotoResponse' });
      if (response.success) {
        log('useSOReprocessos', 'uploadPhoto', 'Photo uploaded successfully', { url: response.dados });
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao enviar foto');
    } catch (err) {
      logError('useSOReprocessos', 'uploadPhoto', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const listByOrderItem = async (orderItemId: number) => {
    log('useSOReprocessos', 'listByOrderItem', 'Fetching reprocessos', { orderItemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista/${orderItemId}`, { responseContract: 'Reprocessos_ListaResponse' });
      if (response.success) {
        reprocessos.value = response.dados || [];
        return reprocessos.value;
      }
      throw new Error(response.error || 'Erro ao buscar reprocessos');
    } catch (err) {
      logError('useSOReprocessos', 'listByOrderItem', 'Error', err);
      error.value = err.message;
      reprocessos.value = [];
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const criar = async (payload: Reprocessos_CriarInput) => {
    log('useSOReprocessos', 'criar', 'Creating reprocesso', payload);
    isLoading.value = true;
    error.value = null;
    try {
      validate('Reprocessos_CriarInput', payload);

      const response = await httpService.post(`${basePath}/criar`, payload, { inputContract: 'Reprocessos_CriarInput', responseContract: 'Reprocessos_CriarResponse' });
      if (response.success) {
        const newId = response.dados;
        log('useSOReprocessos', 'criar', 'Reprocesso created', { id: newId });
        eventBus.emit('reprocesso.created', { id: newId });
        return newId;
      }
      throw new Error(response.error || 'Erro ao criar reprocesso');
    } catch (err) {
      logError('useSOReprocessos', 'criar', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const completar = async (id: any, payload: Reprocessos_CompletarInput) => {
    log('useSOReprocessos', 'completar', 'Completing reprocesso', { id, payload });
    isLoading.value = true;
    error.value = null;
    try {
      validate('Reprocessos_CompletarInput', payload);

      const response = await httpService.post(`${basePath}/completar/${id}`, payload, { inputContract: 'Reprocessos_CompletarInput', responseContract: 'Reprocessos_CompletarResponse' });
      if (response.success) {
        log('useSOReprocessos', 'completar', 'Reprocesso completed', { id });
        eventBus.emit('reprocesso.completed', { id });
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao completar reprocesso');
    } catch (err) {
      logError('useSOReprocessos', 'completar', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    reprocessos,
    uploadPhoto,
    listByOrderItem,
    criar,
    completar,
  };
}
